import { AxiosPromise, AxiosResponse } from 'axios';
import {
    ChannelEvent,
    CompressionFormat,
    DeliveryLogType,
    ExportFileFormats,
    ExportLink,
    InProgressStats,
    LinksClicked,
    Log,
    StatusMessageCategory,
} from '@dev/ee-api/ee-api-models';
import { EventLogParams, EventLogParamsByChannelName } from '@api/store/reports/types';

import { SingleContactPaginationInterface } from '@api/store/contacts/types';
import { request } from './request';

export interface LogsParams {
    statuses?: DeliveryLogType[];
    messageCategory?: StatusMessageCategory[];
    from?: string;
    to?: string;
    channelName?: string;
    limit?: number;
    offset?: number;
    includeEmail?: boolean;
    includeSms?: boolean;
    email?: string;
    ipaddress?: string;
}

// only fetch last events
export const loadLogs = (params: LogsParams): AxiosPromise<Log> => {
    const { Statistics } = request();

    return Statistics.reportsGet(
        params.statuses,
        params.messageCategory as any,
        params.from,
        params.to,
        params.channelName,
        params.limit,
        params.offset,
        params.includeEmail,
        params.includeSms,
        params.email,
        params.ipaddress
    );
};

export const loadEventsGET = (params: LogsParams): AxiosPromise<Log> => {
    const { Statistics } = request();
    return Statistics.reportsEventlogGet(
        params.statuses,
        params.messageCategory,
        params.from,
        params.to,
        params.channelName,
        params.limit,
        // params.offset,
        params.includeEmail,
        params.includeSms,
        params.email,
        params.ipaddress,
        false,
        { timout: 24000 }
    );
};
//only fetch last event
export const loadNotificicationsLog = (params: LogsParams): AxiosPromise<Log> => {
    const { Statistics } = request();
    return Statistics.reportsNotificationsGet(params.statuses as any, params.from, params.to, params.limit, params.offset, params.email);
};

export const loadNotificicationsEventsGET = (params: LogsParams): AxiosPromise<Log> => {
    const { Statistics } = request();
    return Statistics.reportsNotificationsEventlogGet(
        params.statuses,
        params.messageCategory,
        params.from,
        params.to,
        params.limit,
        params.offset,
        false,
        params.email
    );
};

export const loadNotificicationsEventsCountsGET = (params: LogsParams): AxiosPromise<void> => {
    const { Statistics } = request();
    return Statistics.reportsNotificationsEventlogCountsGet(params.from, params.to, params.email, params.limit, params.offset);
};

export const loadInProgressStats = (): AxiosPromise<InProgressStats> => {
    const { Statistics } = request();
    return Statistics.reportsInprogressGet();
};

export type ExportLogsParams = {
    statuses: DeliveryLogType[];
    fileFormat?: ExportFileFormats;
    from?: string;
    to?: string;
    channelName?: string;
    limit?: number;
    offset?: number;
    includeEmail?: boolean;
    includeSms?: boolean;
    messageCategory?: StatusMessageCategory[];
    compressionFormat?: CompressionFormat;
    fileName?: string;
    email?: string;
    onlyLastEvent?: boolean;
    isNotification?: boolean;
};

export const exportEventslogs = (params: ExportLogsParams): AxiosPromise<ExportLink> => {
    const { Statistics } = request();
    const { statuses, messageCategory, from, to, channelName, fileFormat, compressionFormat, fileName, email } = params;
    return Statistics.reportsEventsExportGet(
        statuses,
        from,
        to,
        channelName,
        fileFormat,
        compressionFormat,
        fileName,
        email,
        messageCategory
    );
};

export type ResendParams = {
    messageID: string;
    createNew?: boolean;
    activateContact?: boolean;
};

export const resendByMessageid = (params: ResendParams): AxiosPromise<void> => {
    const { Reports } = request();
    const { messageID, createNew, activateContact } = params;

    return Reports.reportsByMessageidResendPost(messageID, createNew, activateContact);
};

export const loadEventLogCounts = (params: EventLogParams): ReturnType<typeof Reports.reportsEventlogCountsGet> => {
    const { Reports } = request();
    return Reports.reportsEventlogCountsGet(
        params.from,
        params.to,
        params.channelName,
        params.limit,
        params.offset,
        params.includeEmail,
        params.includeSMS,
        params.email,
        params.ipaddress,
        false,
        { timeout: 24000 }
    );
};

export const loadEventLogByChannelName = (params: EventLogParamsByChannelName): AxiosPromise<ChannelEvent[]> => {
    const { Reports } = request();

    return Reports.reportsEventsByChannelnameGet(
        params.channelName,
        params.statuses,
        params.messageCategory,
        params.from,
        params.to,
        params.email,
        params.lastEvent,
        params.limit,
        params.offset,
        params.useMessageCategoryTypeInHeaders
    );
};

export const loadContactLinks = (params: SingleContactPaginationInterface): AxiosPromise<any[]> => {
    const { Reports } = request();
    return Reports.reportsLinktrackingsContactsByEmailGet(params.email, params.limit, params.offset).then(
        (resp): AxiosResponse<LinksClicked[]> => resp
    );
};

export const exportNotificationsEvents = (params: ExportLogsParams): AxiosPromise<ExportLink> => {
    const { Reports } = request();
    const { statuses, fileFormat, from, to, messageCategory, compressionFormat, fileName, email } = params;
    return Reports.reportsNotificationEventsExportGet(statuses, from, to, fileFormat, compressionFormat, fileName, email, messageCategory);
};
